import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReportWidget from './components/ReportWidget';
import { fetchCurrentStatus } from '../../store/reporting/actions';

/**
 * Self-fetching by default (per-org Reporting usage). When the caller
 * passes an explicit {@code data} prop the widget treats it as the source
 * of truth and skips the Redux dispatch entirely — that's the path the
 * Global Dashboard uses to feed a cross-org-aggregated payload through
 * the same JSX.
 */
const CurrentStatusWidget = ({
  organizationId, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber,
  data: externalData, loading: externalLoading, error: externalError,
}) => {
  const dispatch = useDispatch();
  const {
    currentStatus: reduxData,
    currentStatusLoading: reduxLoading,
    currentStatusError: reduxError,
  } = useSelector(state => state.Reporting);
  const externalMode = externalData !== undefined;
  const data = externalMode ? externalData : reduxData;
  const loading = externalMode ? !!externalLoading : reduxLoading;
  const error = externalMode ? externalError : reduxError;
  const { t } = useTranslation();

  // Fetch data on mount and when filters change (skipped in external mode).
  useEffect(() => {
    if (externalMode) return;
    if (!organizationId) return;
    dispatch(fetchCurrentStatus(organizationId, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber));
  }, [externalMode, dispatch, organizationId, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber]);

  const isEmpty = !data || (!data.totalTrackedAssets && !data.doNotTrackCount);

  const formatDate = (val) => {
    if (!val) return '\u2014';
    try {
      // Jackson serializes LocalDateTime as an array: [year, month, day, hour, minute, second, nano]
      if (Array.isArray(val)) {
        const [year, month, day] = val;
        return new Date(year, month - 1, day).toLocaleDateString('en-GB', {
          day: '2-digit', month: '2-digit', year: 'numeric',
        });
      }
      const d = new Date(val);
      if (isNaN(d.getTime())) return '\u2014';
      return d.toLocaleDateString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric',
      });
    } catch {
      return '\u2014';
    }
  };

  const handleRefresh = useCallback(() => {
    if (externalMode) return;  // caller owns refresh in external mode
    if (!organizationId) return;
    dispatch(fetchCurrentStatus(organizationId, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber));
  }, [externalMode, dispatch, organizationId, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber]);

  const hasActiveFilters = criticality || locationId || vendorId || assetId || assetTypeId || contractNumber;

  return (
    <ReportWidget
      title={t('shared.current_status', 'Current Status')}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={hasActiveFilters ? t('shared.no_assets_found_matching_the_selected_fi', 'No assets found matching the selected filters.') : t('reporting.current_status.empty', 'No assets found.')}
      onRefresh={externalMode ? undefined : handleRefresh}
    >
      {/* Summary Row */}
      <div className="row g-3">
        {/* Availability Card */}
        <div className="col-12 col-md-6 col-lg-3">
          <div className="border rounded p-3 h-100">
            <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>{t('reporting.current_status.asset_availability', 'Asset Availability (Last 30 Days)')}</div>
            <span className="fw-bold" style={{ fontSize: '1.75rem' }}>
              {data?.availabilityPercentage?.toFixed(1) ?? '\u2014'}%
            </span>
          </div>
        </div>

        {/* Online Count */}
        <div className="col-6 col-md-3 col-lg-3">
          <div className="border rounded p-3 h-100">
            <div className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>{t('shared.online', 'Online')}</div>
            <div className="d-flex align-items-center gap-2">
              <span
                className="rounded-circle"
                style={{ width: 10, height: 10, backgroundColor: '#10b981', flexShrink: 0 }}
              />
              <span className="fw-bold" style={{ fontSize: '1.75rem' }}>{data?.onlineCount ?? 0}</span>
            </div>
          </div>
        </div>

        {/* Offline Unplanned Count */}
        <div className="col-6 col-md-3 col-lg-3">
          <div className="border rounded p-3 h-100">
            <div className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>{t('shared.offline_unplanned', 'Offline (Unplanned)')}</div>
            <div className="d-flex align-items-center gap-2">
              <span
                className="rounded-circle"
                style={{ width: 10, height: 10, backgroundColor: '#ef4444', flexShrink: 0 }}
              />
              <span className="fw-bold" style={{ fontSize: '1.75rem' }}>{data?.offlineUnplannedCount ?? 0}</span>
            </div>
          </div>
        </div>

        {/* Offline Planned Count */}
        <div className="col-6 col-md-3 col-lg-3">
          <div className="border rounded p-3 h-100">
            <div className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>{t('shared.offline_planned', 'Offline (Planned)')}</div>
            <div className="d-flex align-items-center gap-2">
              <span
                className="rounded-circle"
                style={{ width: 10, height: 10, backgroundColor: '#3b82f6', flexShrink: 0 }}
              />
              <span className="fw-bold" style={{ fontSize: '1.75rem' }}>{data?.offlinePlannedCount ?? 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer info */}
      {data && (
        <div className="d-flex justify-content-between align-items-center mt-3 text-muted" style={{ fontSize: '0.8rem' }}>
          <span>
            {t('reporting.current_status.tracked_assets', '{{count}} tracked asset', { count: data.totalTrackedAssets })}
            {data.totalTrackedAssets !== 1 ? 's' : ''}
            {data.doNotTrackCount > 0 && ` \u00B7 ${t('reporting.current_status.not_tracked', '{{count}} not tracked', { count: data.doNotTrackCount })}`}
          </span>
          <span>{t('shared.last_updated', 'Last updated')}: {formatDate(data.lastUpdated)}</span>
        </div>
      )}
    </ReportWidget>
  );
};

export default CurrentStatusWidget;
